// assets/AllCourses/Exercises/MatchingExercise.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MatchingExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE PAIRS ================= */
  const pairs = Array.isArray(exercise.matchingPairs)
    ? exercise.matchingPairs.map((p) => ({
        question: p?.question?.trim() || "",
        answer: p?.answer?.trim() || "",
        requireCapitalization: !!p?.requireCapitalization,
      }))
    : [];

  /* ================= STATE ================= */
  const [state, setState] = useState({
    matches: {},
    hasInteracted: false,
  });

  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(null);

  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setState({
      matches: {},
      hasInteracted: false,
    });

    setSelectedQuestionIdx(null);
    completedRef.current = false;
  }, [exercise._id]);

  /* ================= RESET FLAG ================= */
  useEffect(() => {
    if (!showResult) completedRef.current = false;
  }, [showResult]);

  /* ================= SHUFFLE ================= */
  const shuffledAnswers = useMemo(() => {
    return pairs
      .map((p, idx) => ({
        answer: p.answer,
        originalIndex: idx,
      }))
      .sort(() => Math.random() - 0.5);
  }, [exercise._id]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updatePairs = (next) => {
    if (isEditMode) onUpdate("matchingPairs", next);
  };

  const updatePairField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...pairs];
    next[idx] = { ...next[idx], [field]: value };
    updatePairs(next);
  };

  /* ================= INTERACTION ================= */
  const handleQuestionClick = (idx) => {
    if (isEditMode || showResult) return;

    setSelectedQuestionIdx(idx);

    setState((prev) => ({
      ...prev,
      hasInteracted: true,
    }));
  };

  const handleAnswerClick = (aIdx) => {
    if (selectedQuestionIdx === null || showResult) return;

    setState((prev) => {
      const nextMatches = {
        ...prev.matches,
        [selectedQuestionIdx]: aIdx,
      };

      const next = {
        matches: nextMatches,
        hasInteracted: true,
      };

      setAttempt(next);
      setSelectedQuestionIdx(null);

      // 🔥 ALWAYS notify parent (progress)
      onComplete(nextMatches, false);

      const allMatched = pairs.every(
        (_, i) => nextMatches[i] !== undefined
      );

      if (allMatched && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(nextMatches);
      }

      return next;
    });
  };

  /* ================= CHECK ================= */
  const checkAnswers = (matches) => {
    const allCorrect = pairs.every((pair, qIdx) => {
      const aIdx = matches[qIdx];
      if (aIdx === undefined) return false;

      const userAnswer = shuffledAnswers[aIdx]?.answer || "";

      return (
        normalize(userAnswer, pair.requireCapitalization) ===
        normalize(pair.answer, pair.requireCapitalization)
      );
    });

    onComplete(matches, allCorrect);
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {pairs.map((p, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <StringField
                label={`Question ${idx + 1}`}
                value={p.question}
                onChange={(v) => updatePairField(idx, "question", v)}
              />

              <StringField
                label={`Answer ${idx + 1}`}
                value={p.answer}
                onChange={(v) => updatePairField(idx, "answer", v)}
              />

              <CapitalizationRule
                checked={p.requireCapitalization}
                onChange={(c) =>
                  updatePairField(idx, "requireCapitalization", c)
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updatePairs(pairs.filter((_, i) => i !== idx))
                }
              >
                Remove
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updatePairs([
              ...pairs,
              { question: "", answer: "", requireCapitalization: false },
            ])
          }
        >
          Add Pair
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!pairs.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No matching pairs available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">Match the Pairs</CardTitle>
        <CardDescription>
          Click question → then matching answer
        </CardDescription>
      </CardHeader>

      <CardContent className="grid md:grid-cols-2 gap-8">
        {/* QUESTIONS */}
        <div className="space-y-3">
          {pairs.map((p, qIdx) => {
            const matchedIdx = state.matches[qIdx];

            const userAnswer =
              matchedIdx !== undefined
                ? shuffledAnswers[matchedIdx]?.answer
                : null;

            const isCorrect =
              userAnswer &&
              normalize(userAnswer, p.requireCapitalization) ===
                normalize(p.answer, p.requireCapitalization);

            return (
              <Button
                key={qIdx}
                variant="outline"
                onClick={() => handleQuestionClick(qIdx)}
                className={cn(
                  "w-full justify-start text-left",

                  selectedQuestionIdx === qIdx && "bg-accent",

                  showResult &&
                    matchedIdx !== undefined &&
                    isCorrect &&
                    "bg-green-100 border-green-400",

                  showResult &&
                    matchedIdx !== undefined &&
                    !isCorrect &&
                    "bg-red-100 border-red-400"
                )}
              >
                {p.question}

                {showResult && matchedIdx !== undefined && (
                  <span className="ml-auto text-sm">
                    {isCorrect ? "✔" : `✘ → ${p.answer}`}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* ANSWERS */}
        <div className="space-y-3">
          {shuffledAnswers.map((item, aIdx) => {
            const used = Object.values(state.matches).includes(aIdx);

            return (
              <Button
                key={aIdx}
                variant="outline"
                disabled={used || showResult}
                onClick={() => handleAnswerClick(aIdx)}
                className={cn(
                  "w-full justify-start",
                  used && "opacity-50"
                )}
              >
                {item.answer}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}